import React, { useEffect, useState } from 'react';
import './LocationOverlay.css';
import { Button } from '../../design-system';
import { fetchStores } from '../../services/api';
import { Store } from '../../types/location.types';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import StoreOpeningHours from '../StoreOpeningHours/StoreOpeningHours';
import { isDemoModeActive, DEMO_STORE_ID } from '../../utils/demoMode';
import { rememberedStoreId } from '../../utils/locationChoice';

interface LocationOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: Store) => void;
  isDemoMode?: boolean;
  demoStoreId?: number;
}

export const LocationOverlay: React.FC<LocationOverlayProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  isDemoMode: isDemoModeProp = false,
  demoStoreId
}) => {
  // Use prop OR session flag — covers both cases
  const isDemoMode = isDemoModeProp || isDemoModeActive();
  const effectiveDemoStoreId = demoStoreId || DEMO_STORE_ID;
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The location this phone remembers, tagged "Last visited" (G49): the client
  // sees which one the site was about to use. Nothing is ticked on opening.
  const [lastVisitedId, setLastVisitedId] = useState<number | null>(null);

  const loadStores = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchStores();
      setStores(Array.isArray(data) ? data : []);
      
    } catch (err) {
      console.error('Error in LocationOverlay:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Unable to load locations. Please check your internet connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedStore(null);
      setLastVisitedId(rememberedStoreId());
      loadStores();
    }
  }, [isOpen]);

  const handleStoreSelect = (store: Store) => {
    if (isDemoMode && store.id !== effectiveDemoStoreId) {
      return;
    }
    setSelectedStore(store);
  };

  const handleConfirmSelection = () => {
    if (selectedStore) {
      onLocationSelect(selectedStore);
      onClose();
    }
  };

  const formatAddress = (store: Store) => {
    if (!store.address) {
      return 'Address not available at the moment';
    }
    
    // Use formattedAddress if available (from API)
    if (store.address.formattedAddress) {
      return store.address.formattedAddress;
    }
    
    // Fallback to constructing from street and addressLine1
    const parts = [
      store.address.street,
      store.address.addressLine1
    ].filter(Boolean);
    
    return parts.join(' ') || 'Address not available at the moment';
  };

  // Add retry button handler
  const handleRetry = () => {
    loadStores();
  };

  if (!isOpen) return null;

  return (
    <div className="location-overlay">
      <div className="location-overlay-content">
        <div className="location-overlay-header">
          <div className="header-title">Select a location</div>
          <Button variant="menu" onClick={onClose} className="close-button">
            <div className="close-icon" />
          </Button>
        </div>

        <div className="location-overlay-body">
          {loading ? (
            <div className="loading-state">
              <LoadingSpinner />
              <p>Loading locations...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">!</div>
              <p>{error}</p>
              <Button 
                variant="primary" 
                onClick={handleRetry}
                className="retry-button"
              >
                Try Again
              </Button>
            </div>
          ) : stores.length === 0 ? (
            <div className="empty-state">
              <p>No locations available</p>
            </div>
          ) : (
            <>
              {isDemoMode && (
                <div className="demo-store-error">
                  You are in demo mode. Only the demo store is available.
                </div>
              )}
              <div className="location-list">
                {stores.map((store) => {
                  const isDisabled = isDemoMode && store.id !== effectiveDemoStoreId;
                  return (
                    <div
                      key={store.id}
                      className={`location-option ${selectedStore?.id === store.id ? 'selected' : ''} ${isDisabled ? 'demo-disabled' : ''}`}
                      onClick={isDisabled ? undefined : () => handleStoreSelect(store)}
                      style={isDisabled ? { pointerEvents: 'none' } : undefined}
                    >
                      <img
                        src={(store.type || 'shop') === 'shop' ? '/assets/groomingshop.png' : '/assets/mobilevan.png'}
                        alt={`${store.name} location`}
                        className="location-image"
                      />
                      <div className="location-info">
                        {lastVisitedId !== null && Number(store.id) === lastVisitedId && (
                          <span className="location-last-visited" data-last-visited>Last visited</span>
                        )}
                        <div className="location-name">{store.name}</div>
                        <div className="location-address">{formatAddress(store)}</div>
                        {store.businessHours && store.businessHours.length > 0 && (
                          <StoreOpeningHours businessHours={store.businessHours} variant="compact" />
                        )}
                      </div>
                      {isDisabled && (
                        <div className="demo-lock-badge">Not available in demo</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button
                variant="primary"
                isEnabled={selectedStore !== null}
                onClick={handleConfirmSelection}
                className="select-button"
              >
                Next
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export {}; 